import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { stageLabel } from "@/lib/stages";

const COMMODITIES = ["Crude Oil (Brent)", "Crude Oil (WTI)", "Gasoil EN 590", "Jet A-1",
                     "Gasoline (RON 95)", "ICUMSA 45 Sugar", "Green Coffee — Arabica",
                     "Green Coffee — Robusta"];
const UNITS = ["MT", "BBL", "KG", "Bags", "Containers"];
const INCOTERMS = ["FOB", "CIF", "CFR", "EXW", "DAP", "DDP"];

export default function DealIntake() {
  const [form, setForm] = useState({
    commodity: "ICUMSA 45 Sugar", specs: "", target_price: "", volume: "",
    unit: "MT", incoterm: "CIF", delivery_port: "", notes: "",
    icpo_reference: "", icpo_issue_date: "",
    bcl_reference: "", bcl_issuing_bank: "", bcl_swift: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [deals, setDeals] = useState([]);

  const reloadDeals = useCallback(
    () => api.get("/deals").then((r) => setDeals(r.data)).catch(() => {}),
    [],
  );
  useEffect(() => { reloadDeals(); }, [reloadDeals]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(null); setSubmitting(true);
    try {
      const payload = { ...form, target_price: Number(form.target_price), volume: Number(form.volume) };
      const { data } = await api.post("/deals", payload);
      setSuccess(data);
      setForm({
        ...form, specs: "", target_price: "", volume: "", delivery_port: "", notes: "",
        icpo_reference: "", icpo_issue_date: "", bcl_reference: "", bcl_issuing_bank: "", bcl_swift: "",
      });
      reloadDeals();
    } catch (e) {
      setError(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout testid="deal-intake-page">
      <div className="p-8 md:p-12 max-w-6xl">
        <div className="overline">Step 01 — Secure Intake · ICPO + Bank Comfort Letter</div>
        <h1 className="h-serif text-5xl text-ink mt-3">Submit an ICPO</h1>
        <p className="text-sub mt-4 max-w-2xl">
          The Irrevocable Corporate Purchase Order and your Bank Comfort Letter trigger
          the deal-flow pipeline. The platform sanitizes your identifying information
          before any supplier sees the request.
        </p>

        {success && (
          <div className="mt-6 border-l-4 border-success bg-parchment p-5" data-testid="deal-success">
            <div className="overline !text-success">ICPO received — soft probe initiated</div>
            <div className="text-sm text-ink mt-1">
              Deal <span className="font-mono">{success.deal_id}</span> for {success.commodity} —
              the trade desk has been notified.
            </div>
            <Link to={`/dashboard/deals/${success.deal_id}`} className="link mt-3 inline-block text-ink">
              View deal pipeline →
            </Link>
          </div>
        )}
        {error && <div className="mt-6 border-l-4 border-danger bg-parchment p-5 text-danger" data-testid="deal-error">{error}</div>}

        <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-7" data-testid="deal-form">
          {/* ICPO + BCL */}
          <div className="md:col-span-2 mt-2">
            <div className="overline">ICPO &amp; Bank Comfort Letter</div>
            <h2 className="h-serif text-2xl text-ink mt-1">Proof of intent &amp; funds</h2>
          </div>
          <Field label="ICPO reference number">
            <input required className="input-line" value={form.icpo_reference}
                   onChange={set("icpo_reference")} placeholder="ICPO-2026-001"
                   data-testid="deal-icpo-ref" />
          </Field>
          <Field label="ICPO issue date">
            <input type="date" className="input-line" value={form.icpo_issue_date}
                   onChange={set("icpo_issue_date")} data-testid="deal-icpo-date" />
          </Field>
          <Field label="BCL reference">
            <input required className="input-line" value={form.bcl_reference}
                   onChange={set("bcl_reference")} placeholder="BCL-XXX-2026"
                   data-testid="deal-bcl-ref" />
          </Field>
          <Field label="BCL issuing bank">
            <input required className="input-line" value={form.bcl_issuing_bank}
                   onChange={set("bcl_issuing_bank")} placeholder="HSBC Hong Kong"
                   data-testid="deal-bcl-bank" />
          </Field>
          <Field label="BCL bank SWIFT / BIC (optional)">
            <input className="input-line" value={form.bcl_swift}
                   onChange={set("bcl_swift")} placeholder="HSBCHKHHHKH"
                   data-testid="deal-bcl-swift" />
          </Field>

          {/* Commercial terms */}
          <div className="md:col-span-2 mt-6 border-t border-line pt-6">
            <div className="overline">Commercial terms</div>
            <h2 className="h-serif text-2xl text-ink mt-1">What you intend to buy</h2>
          </div>
          <Field label="Commodity">
            <select className="input-line" value={form.commodity} onChange={set("commodity")} data-testid="deal-commodity">
              {COMMODITIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Volume">
            <div className="flex gap-3">
              <input type="number" required className="input-line tabular" min="0" step="any"
                     value={form.volume} onChange={set("volume")} placeholder="25000"
                     data-testid="deal-volume" />
              <select className="input-line !w-28" value={form.unit} onChange={set("unit")} data-testid="deal-unit">
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
          </Field>
          <Field label="Technical specifications" full>
            <textarea required rows={3} className="input-line resize-none" value={form.specs}
                      onChange={set("specs")} placeholder="e.g. ICUMSA 45, polarization 99.8°, moisture ≤0.04%, 50kg PP bags"
                      data-testid="deal-specs" />
          </Field>
          <Field label="Target price (USD per unit)">
            <input type="number" required className="input-line tabular" min="0" step="any"
                   value={form.target_price} onChange={set("target_price")} placeholder="540"
                   data-testid="deal-price" />
          </Field>
          <Field label="Incoterm">
            <select className="input-line" value={form.incoterm} onChange={set("incoterm")} data-testid="deal-incoterm">
              {INCOTERMS.map((i) => <option key={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Delivery port">
            <input required className="input-line" value={form.delivery_port}
                   onChange={set("delivery_port")} placeholder="Rotterdam" data-testid="deal-port" />
          </Field>
          <Field label="Additional notes" full>
            <textarea rows={3} className="input-line resize-none" value={form.notes}
                      onChange={set("notes")} placeholder="Payment terms preference · inspection · lay-can window"
                      data-testid="deal-notes" />
          </Field>

          <div className="md:col-span-2 mt-2 flex items-center justify-between">
            <p className="text-xs text-sub">Your identity is stripped before this reaches any supplier.</p>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="deal-submit">
              {submitting ? "Submitting ICPO…" : "Submit ICPO →"}
            </button>
          </div>
        </form>

        <section className="mt-16">
          <div className="overline">Your deals</div>
          <h2 className="h-serif text-2xl text-ink mt-1">History</h2>
          <div className="mt-5 border border-line bg-parchment divide-y divide-line">
            {deals.length === 0 && <div className="p-6 text-sub text-sm">No deals yet.</div>}
            {deals.map((d) => (
              <Link to={`/dashboard/deals/${d.deal_id}`} key={d.deal_id}
                    className="block p-5 hover:bg-parchment2 transition-colors"
                    data-testid={`deal-history-${d.deal_id}`}>
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-4">
                    <div className="h-serif text-lg text-ink">{d.commodity}</div>
                    <div className="font-mono text-[10px] text-sub mt-1">{d.deal_id}</div>
                  </div>
                  <div className="col-span-3 text-sm text-sub tabular">
                    {Number(d.volume).toLocaleString()} {d.unit}
                  </div>
                  <div className="col-span-3 text-sm text-sub">{d.incoterm} · {d.delivery_port}</div>
                  <div className="col-span-2 font-mono text-xs text-oxblood text-right uppercase">{stageLabel(d.stage)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children, full = false }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="overline block mb-2">{label}</span>
      {children}
    </label>
  );
}
